import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Radio, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
    GET_INHUB_SORTING_HOURLY_TREND,
    type GetInhubSortingHourlyTrendResponse
} from '../graphql/queries/getInhubSortingHourlyTrend';
import type { InhubSortingDimension, InhubSortingMetric } from '../graphql/types';
import { fromWireIso } from '../graphql/timeRange';

const LINE_COLORS = ['#5B8FF9', '#5AD8A6', '#5D7092', '#F6BD16', '#E8684A', '#6DC8EC'];
const TREND_TOP_N = 6;

interface Props {
    hubId: number;
    rangeStart: string;
    rangeEnd: string;
    metric: InhubSortingMetric;
    metricLabel: string;
}

interface ChartPoint {
    hour: string;
    [series: string]: string | number;
}

const HourlyTrendChart: React.FC<Props> = ({ hubId, rangeStart, rangeEnd, metric, metricLabel }) => {
    const { t } = useTranslation();
    const I18N = 'wms.delivery.inhubDashboard.sorting';
    const [dimension, setDimension] = useState<InhubSortingDimension>('ACCOUNT');

    const { data, loading } = useQuery<GetInhubSortingHourlyTrendResponse>(GET_INHUB_SORTING_HOURLY_TREND, {
        variables: { filter: { hubId, rangeStart, rangeEnd, metric, dimension } },
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true
    });

    const buckets = data?.getInhubSortingHourlyTrend?.buckets ?? [];

    // 后端只返回 DB 中有数据的小时；前端按 [rangeStart, rangeEnd] 逐小时补齐时间轴，
    // 缺失小时各 series 值为 0，避免 x 轴不连续 + 跨日 HH:00 撞车
    const { chartData, seriesKeys } = useMemo(() => {
        if (buckets.length === 0) return { chartData: [] as ChartPoint[], seriesKeys: [] as string[] };
        const keysOrdered: string[] = [];
        const seen = new Set<string>();
        buckets.forEach((b) => {
            b.series.forEach((s) => {
                if (!seen.has(s.name)) {
                    seen.add(s.name);
                    keysOrdered.push(s.name);
                }
            });
        });
        const unGroupedLabel = t('wms.delivery.inhubDashboard.unGrouped');
        const bucketByHour = new Map<number, typeof buckets[number]>();
        buckets.forEach((b) => {
            bucketByHour.set(fromWireIso(b.bucketStart).startOf('hour').valueOf(), b);
        });
        // bucketStart 与 rangeStart/rangeEnd 都是墙上时间的传输编码，须按同一 zone 解回原始数字
        const start = fromWireIso(rangeStart).startOf('hour');
        const end = fromWireIso(rangeEnd).startOf('hour');
        const points: ChartPoint[] = [];
        let cursor = start;
        while (cursor.isBefore(end) || cursor.isSame(end)) {
            const bucket = bucketByHour.get(cursor.valueOf());
            const point: ChartPoint = { hour: cursor.format('MM-DD HH:00') };
            keysOrdered.forEach((k) => {
                const display = (k === '' || k === '未分组') ? unGroupedLabel : k;
                const found = bucket?.series.find((s) => s.name === k);
                point[display] = found ? found.value : 0;
            });
            points.push(point);
            cursor = cursor.add(1, 'hour');
        }
        const displayKeys = keysOrdered.map((k) => ((k === '' || k === '未分组') ? unGroupedLabel : k));
        return { chartData: points, seriesKeys: displayKeys };
    }, [buckets, rangeStart, rangeEnd, t]);

    return (
        <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800">{t(`${I18N}.trend.title`)}</span>
                    <span className="text-xs text-gray-700">· {metricLabel}</span>
                    <span className="text-xs text-gray-500">· {t(`${I18N}.topNHint`, { topN: TREND_TOP_N })}</span>
                </div>
                <Radio.Group
                    value={dimension}
                    onChange={(e) => setDimension(e.target.value)}
                    size="small"
                >
                    <Radio.Button value="ACCOUNT">{t(`${I18N}.dimension.byAccount`)}</Radio.Button>
                    <Radio.Button value="TEAM">{t(`${I18N}.dimension.byTeam`)}</Radio.Button>
                </Radio.Group>
            </div>
            <div className="h-64">
                {loading && chartData.length === 0 ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#262626' }} minTickGap={40} />
                            <YAxis tick={{ fontSize: 11, fill: '#262626' }} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {seriesKeys.map((key, idx) => (
                                <Line
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default HourlyTrendChart;
